import axios from 'axios';

/**
 * Unified Authentication Service
 * Handles authentication for admin, client, and contentMakers apps
 */
class AuthService {
    constructor(config = {}) {
        this.baseURL = config.baseURL || process.env.REACT_APP_API_URL;
        this.appType = config.appType || 'client'; // 'admin', 'client', 'contentMakers'
        this.accessToken = null;
        this.refreshTimer = null;
        this.isRefreshing = false;
        this.failedQueue = [];
        this.isLoggingOut = false;
        
        // App-specific configurations
        this.config = {
            storageKey: this.getStorageKey(),
            requireAdminRole: config.requireAdminRole || false,
            deviceNameSuffix: this.getDeviceNameSuffix(),
            enableRegistration: config.enableRegistration !== false,
            reduxIntegration: config.reduxIntegration || false,
            onLogout: config.onLogout || null
        };
        
        // Initialize from localStorage on startup
        this.initializeAuth();
    }

    // Get storage key based on app type
    getStorageKey() {
        return this.appType === 'admin' ? 'adminUser' : 'user';
    }

    // Get device name suffix based on app type
    getDeviceNameSuffix() {
        switch (this.appType) {
            case 'admin':
                return ' (Admin)';
            case 'contentMakers':
                return ' (Content Maker)';
            default:
                return '';
        }
    }

    // Generate device name
    generateDeviceName() {
        const browser = navigator.userAgent.includes('Chrome') ? 'Chrome' : 
                       navigator.userAgent.includes('Firefox') ? 'Firefox' : 
                       navigator.userAgent.includes('Safari') ? 'Safari' : 'Browser';
        
        const platform = navigator.platform.includes('Win') ? 'Windows' : 
                         navigator.platform.includes('Mac') ? 'macOS' : 
                         navigator.platform.includes('Linux') ? 'Linux' : 'Desktop';
        
        return `${browser} on ${platform}${this.config.deviceNameSuffix}`;
    }

    // Initialize authentication state from localStorage
    initializeAuth() {
        try {
            const user = localStorage.getItem(this.config.storageKey);
            if (user) {
                const userData = JSON.parse(user);
                if (userData && userData.accessToken) {
                    this.accessToken = userData.accessToken;
                    // Don't setup auto refresh on initialization - only after successful login
                }
            }
        } catch (error) {
            console.error('Failed to initialize auth:', error);
            localStorage.removeItem(this.config.storageKey);
        }
    }

    // Create axios instance with interceptors
    createAxiosInstance() {
        const instance = axios.create({
            baseURL: this.baseURL,
            withCredentials: true // Important for cookies
        });

        // Request interceptor to add token
        instance.interceptors.request.use(
            (config) => {
                if (this.accessToken) {
                    config.headers.Authorization = `Bearer ${this.accessToken}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        // Response interceptor to handle token refresh
        instance.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;

                if (error.response?.status === 401 && !originalRequest._retry) {
                    originalRequest._retry = true;

                    if (this.isRefreshing) {
                        // If already refreshing, queue the request
                        return new Promise((resolve, reject) => {
                            this.failedQueue.push({ resolve, reject });
                        }).then(() => {
                            return instance(originalRequest);
                        });
                    }

                    this.isRefreshing = true;

                    try {
                        await this.refreshAccessToken();
                        this.processQueue(null);
                        return instance(originalRequest);
                    } catch (refreshError) {
                        this.processQueue(refreshError);
                        // Only call logout if not already in a logout process
                        if (!this.isLoggingOut) {
                            this.logout();
                        }
                        throw refreshError;
                    } finally {
                        this.isRefreshing = false;
                    }
                }

                return Promise.reject(error);
            }
        );

        return instance;
    }

    // Process queued requests after token refresh
    processQueue(error) {
        this.failedQueue.forEach(({ resolve, reject }) => {
            if (error) {
                reject(error);
            } else {
                resolve();
            }
        });
        
        this.failedQueue = [];
    }

    // Login user
    async login(email, password, deviceName = null) {
        try {
            // Use generated device name if none provided
            const finalDeviceName = deviceName || this.generateDeviceName();
            
            // Add a small delay to ensure proper cookie handling
            await new Promise(resolve => setTimeout(resolve, 100));
            
            const response = await axios.post(`${this.baseURL}/user/login`, {
                email,
                password,
                deviceName: finalDeviceName
            }, { 
                withCredentials: true,
                // Add explicit headers to ensure proper request handling
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });

            const { user, accessToken, expiresAt } = response.data;

            // Check admin role requirement
            if (this.config.requireAdminRole && (!user.roles || !user.roles.includes('admin'))) {
                throw new Error('Access denied. Admin privileges required.');
            }

            // Store user and token
            const userData = { ...user, accessToken, expiresAt };
            localStorage.setItem(this.config.storageKey, JSON.stringify(userData));
            
            this.accessToken = accessToken;
            this.setupTokenRefresh();

            return userData;
        } catch (error) {
            throw new Error(error.response?.data?.message || error.message || 'Login failed');
        }
    }

    // Refresh access token
    async refreshAccessToken() {
        try {
            const response = await axios.post(`${this.baseURL}/user/refresh`, {}, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });

            const { accessToken } = response.data;
            this.accessToken = accessToken;

            // Update stored user data
            const user = localStorage.getItem(this.config.storageKey);
            if (user) {
                const userData = JSON.parse(user);
                userData.accessToken = accessToken;
                localStorage.setItem(this.config.storageKey, JSON.stringify(userData));
            }

            this.setupTokenRefresh();
            
            return accessToken;
        } catch (error) {
            throw new Error('Token refresh failed');
        }
    }

    // Setup automatic token refresh
    setupTokenRefresh() {
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
        }

        // Only setup refresh timer if we have a valid session
        const user = this.getCurrentUser();
        if (!user || !this.accessToken) {
            return;
        }

        // Refresh token 2 minutes before expiry (13 minutes after issue)
        const refreshTime = 13 * 60 * 1000; // 13 minutes
        
        this.refreshTimer = setTimeout(async () => {
            try {
                await this.refreshAccessToken();
            } catch (error) {
                console.error('Auto refresh failed:', error);
                this.clearAuthData();
            }
        }, refreshTime);
    }

    // Logout user
    async logout() {
        // Prevent infinite logout loops
        if (this.isLoggingOut) {
            return;
        }
        
        this.isLoggingOut = true;
        
        try {
            // Use a clean axios instance without interceptors for logout
            await axios.post(`${this.baseURL}/user/logout`, {}, {
                withCredentials: true,
                headers: this.accessToken ? { Authorization: `Bearer ${this.accessToken}` } : {}
            });
        } catch (error) {
            console.error('Logout request failed:', error);
        } finally {
            this.clearAuthData();
            this.isLoggingOut = false;
        }
    }

    // Logout from all devices
    async logoutAll() {
        // Prevent infinite logout loops
        if (this.isLoggingOut) {
            return;
        }
        
        this.isLoggingOut = true;
        
        try {
            // Use a clean axios instance without interceptors for logout
            await axios.post(`${this.baseURL}/user/logoutAll`, {}, {
                withCredentials: true,
                headers: this.accessToken ? { Authorization: `Bearer ${this.accessToken}` } : {}
            });
        } catch (error) {
            console.error('Logout all request failed:', error);
        } finally {
            this.clearAuthData();
            this.isLoggingOut = false;
        }
    }

    // Clear authentication data
    clearAuthData() {
        this.accessToken = null;
        localStorage.removeItem(this.config.storageKey);
        
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }

        // Handle logout based on configuration
        if (this.config.onLogout) {
            this.config.onLogout();
        } else if (this.config.reduxIntegration) {
            // Try Redux integration for client and contentMakers
            try {
                const { store } = require('../_store');
                const { authActions } = require('../_store/auth.slice');
                store.dispatch(authActions.logout());
            } catch (error) {
                console.warn('Could not dispatch logout action, falling back to window navigation');
                this.fallbackLogout();
            }
        } else {
            this.fallbackLogout();
        }
    }

    // Fallback logout method
    fallbackLogout() {
        if (typeof window !== 'undefined') {
            window.location.href = '/login';
        }
    }

    // Get current user
    getCurrentUser() {
        try {
            const user = localStorage.getItem(this.config.storageKey);
            return user ? JSON.parse(user) : null;
        } catch (error) {
            console.error('Failed to parse user data:', error);
            localStorage.removeItem(this.config.storageKey);
            return null;
        }
    }

    // Check if user is authenticated
    isAuthenticated() {
        const user = this.getCurrentUser();
        if (!this.accessToken || !user) {
            return false;
        }
        
        // Additional admin check for admin app
        if (this.config.requireAdminRole) {
            return user.roles?.includes('admin') || false;
        }
        
        return true;
    }

    // Get access token
    getAccessToken() {
        return this.accessToken;
    }

    // Get user sessions
    async getUserSessions() {
        const instance = this.createAxiosInstance();
        const response = await instance.get('/user/sessions');
        return response.data;
    }

    // Revoke specific session
    async revokeSession(sessionId) {
        const instance = this.createAxiosInstance();
        const response = await instance.delete(`/user/sessions/${sessionId}`);
        return response.data;
    }

    // Register new user
    async register(name, email, password) {
        if (!this.config.enableRegistration) {
            throw new Error('User registration is not available in this interface');
        }
        
        try {
            const response = await axios.post(`${this.baseURL}/user/signup`, {
                name,
                email,
                password
            });

            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Registration failed');
        }
    }
}

export default AuthService;